import { useMutation } from "@apollo/client";
import { useSnapshot } from "valtio";
import { refreshSiteGeometryStore } from "src/js/stores/siteGeometry/actions/refreshSiteGeometryStore";
import { mapStore } from "src/js/stores/map/store";
import { LogEventCreatedSiteEventLocation, logEventV2 } from "react-migration/lib/util/logEventV2";
import { evictStagesCache } from "react-migration/domains/sites/apollo/sites-api/util/evictStagesCache";
import { SaveSiteLayer } from "react-migration/domains/sites/typings/SaveSiteLayer";
import { routedClient } from "react-migration/lib/persistence/apollo";
import createSites from "../graphql/createSites.gql";
import { Site } from "../typings/Site";

export type CreatedSite = Record<string, unknown> & {
  _id: string;
  _stage?:
    | {
        _id: string;
      }
    | undefined
    | null;
};

export interface CreateSitesQueryData {
  createSites: {
    id: string;
    saved_site: CreatedSite;
    geometry_info: "OK" | "BAD_GEOMETRY" | "NO_POLYGON";
  }[];
}

export interface CreateSitesQueryVariables {
  sites: {
    geometry: GeoJSON.Point | GeoJSON.Polygon | GeoJSON.MultiPolygon;
    source: { layer: SaveSiteLayer };
  }[];
  stageId?: string;
}

interface UseSaveSitesMutationArgs {
  sites: Site[];
  stageId?: string;
  source: LogEventCreatedSiteEventLocation;
  selectionType?: string;
  cardName?: string;
  onCompleted?: () => void;
}

export function useSaveSiteMutation({
  sites,
  stageId,
  source,
  selectionType,
  cardName,
  onCompleted,
}: UseSaveSitesMutationArgs) {
  const { settings } = useSnapshot(mapStore);
  const currentBundleId = settings.activeLayer;

  return useMutation<CreateSitesQueryData, CreateSitesQueryVariables>(createSites, {
    client: routedClient,
    variables: {
      sites: sites,
      stageId,
    },
    onCompleted({ createSites }, { client, variables }: any) {
      if (mapStore.googleMap) {
        refreshSiteGeometryStore(mapStore.googleMap);
      }

      // This monstrosity is here to match what is currently used in the backend and this should be changed so that we don't have to do this.
      const siteSavingId = `${variables.sites[0].geometry.coordinates[1]},${variables.sites[0].geometry.coordinates[0]}`;

      client.cache.modify({
        id: `SiteSaving:${siteSavingId}`,
        fields: {
          saved_site() {
            return createSites[0].saved_site;
          },
        },
      });

      evictStagesCache();

      logEventV2({ name: "Sites layer button click", properties: { type: "save" } });
      logEventV2({
        name: "Created site",
        properties: {
          source: currentBundleId,
          eventLocation: source,
          selectionType,
          cardName,
        },
      });

      onCompleted?.();
    },
  });
}
